/**
 * ArousalReactionCard — будност и реакция (ретикуларна формация, Nerve2).
 * Чете тестa „Време на реакция" и го сверява със съня, DIL и кортизола.
 * Данни: GET /api/cognitive/arousal
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Gauge, Loader2, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BAND = { green: "#22C55E", yellow: "#EAB308", orange: "#F97316", red: "#EF4444" };

const R = ({ label, r, isDark }) => {
  const color = r == null ? "opacity-50"
    : Math.abs(r) >= 0.5 ? "text-rose-500"
    : Math.abs(r) >= 0.3 ? "text-amber-500" : "opacity-60";
  return (
    <div className={`p-2 rounded-lg ${isDark ? "bg-white/5" : "bg-slate-50"}`}>
      <div className="text-[9px] uppercase font-bold opacity-60">{label}</div>
      <div className={`text-base font-bold ${color}`}>{r != null ? `r=${r}` : "—"}</div>
    </div>
  );
};

export default function ArousalReactionCard({ isDark = false, cardCls = "", onStartTest, refreshKey = 0 }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const border = isDark ? "border-white/10" : "border-gray-200";

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/cognitive/arousal`)
      .then((r) => { if (alive) setD(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [refreshKey]);

  if (loading) {
    return (
      <div className={cardCls} data-testid="arousal-card">
        <div className="text-[12px] opacity-60 inline-flex items-center gap-1.5">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Зареждам будността…
        </div>
      </div>
    );
  }
  if (!d) return null;

  return (
    <div className={cardCls} data-testid="arousal-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold inline-flex items-center gap-1.5">
            <Gauge className="w-4 h-4 text-indigo-500" /> Будност и реакция
          </div>
          {d.has_data ? (
            <p className="text-[12px] font-bold mt-1" style={{ color: BAND[d.band] }}
              data-testid="arousal-label">
              {d.label_bg} · {d.score}/100
            </p>
          ) : (
            <p className="text-[12px] opacity-75 mt-1" data-testid="arousal-gap">{d.gap_bg}</p>
          )}
          <p className="text-[11px] opacity-70 mt-1">{d.explain_bg}</p>
        </div>
        {onStartTest && (
          <button type="button" onClick={onStartTest}
            className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-[12px] font-semibold inline-flex items-center gap-1.5 shrink-0"
            data-testid="arousal-start-test">
            <Zap className="w-3.5 h-3.5" /> {d.has_data ? "Нов тест" : "Направи теста"}
          </button>
        )}
      </div>

      {d.has_data && (
        <>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="arousal-metrics">
            {[["Средно време", `${d.mean_rt_ms} ms`],
              ["Най-бързо", `${d.best_rt_ms} ms`],
              ["Пропуски", `${d.lapses}/${d.trials}`],
              ["Разсейване", d.variability_ms != null ? `±${d.variability_ms} ms` : "—"]].map(([l, v]) => (
              <div key={l} className={`p-2 rounded-lg ${isDark ? "bg-white/5" : "bg-slate-50"}`}>
                <div className="text-[9px] uppercase font-bold opacity-60">{l}</div>
                <div className="text-sm font-bold tabular-nums">{v}</div>
              </div>
            ))}
          </div>

          {d.drivers?.length > 0 && (
            <ul className="mt-2 space-y-1" data-testid="arousal-drivers">
              {d.drivers.map((x, i) => (
                <li key={i} className="text-[11px]">
                  <span className="font-bold">{x.label_bg}</span>
                  <span className="opacity-80"> — {x.text_bg}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-3 grid grid-cols-3 gap-2" data-testid="arousal-correlations">
            <R label="Реакция ↔ сън" r={d.vs_sleep_r} isDark={isDark} />
            <R label="Реакция ↔ DIL" r={d.vs_dil_r} isDark={isDark} />
            <R label="Реакция ↔ мъгла" r={d.vs_fog_r} isDark={isDark} />
          </div>

          {d.links?.map((l, i) => (
            <div key={i} className={`mt-2 p-2 rounded-lg border ${border}`}
              data-testid={`arousal-link-${l.twin}`}>
              <div className="text-[11px] font-bold">{l.title_bg}</div>
              <div className="text-[10px] opacity-85">{l.why_bg}</div>
              <div className="text-[10px] font-medium mt-0.5">{l.action_bg}</div>
              <Link to={l.route}
                className="text-[10px] font-bold text-indigo-600 dark:text-indigo-300 inline-flex items-center gap-1 mt-0.5"
                data-testid={`arousal-link-cta-${l.twin}`}>
                Отвори {l.twin_bg} <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          ))}

          <div className="mt-3" data-testid="arousal-levers">
            <div className="text-[11px] font-bold">Лостове за будност</div>
            <ul className="mt-1 space-y-1">
              {d.levers.map((x, i) => (
                <li key={i} className="text-[11px]">
                  <span className="font-bold">{x.title_bg}</span>
                  <span className="opacity-80"> — {x.why_bg}</span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
      <p className="text-[9px] opacity-50 mt-2">{d.note_bg}</p>
    </div>
  );
}
